export enum PermissionName {
  ViewCompany = 'view_company',
  ManageCompany = 'manage_company',
  ViewCompanies = 'view_companies',
  DeleteCompany = 'delete_company',
  UsersView = 'users_view',
  UsersInvite = 'users_invite',
  UsersEdit = 'users_edit',
  // VRG-610 — deliberately NOT folded into `users_edit`. Two reasons, and the
  // first is decisive on its own: `users_edit` is granted to `company_admin`
  // ONLY, so gating an operator-triggered reset on it would lock out exactly the
  // internal staff (platform_admin / platform_manager) the capability exists for.
  // Second, sending someone a password-reset link is a credential action, not a
  // profile edit — a role that may correct a job title should not thereby be able
  // to initiate a credential change for another person.
  UsersResetPassword = 'users_reset_password',
  UsersDelete = 'users_delete',
  ViewRoles = 'view_roles',
  ManageRoles = 'manage_roles',
  AssignUsers = 'assign_users',
  InstallationView = 'installation_view',
  InstallationEdit = 'installation_edit',
  InstallationSetPrimary = 'installation_set_primary',
  InstallationEditSubdomain = 'installation_edit_subdomain',
  BillingView = 'billing_view',
  BillingActivationSettings = 'billing_activation_settings',
  BillingPurchaseAddons = 'billing_purchase_addons',
  BillingEditAddons = 'billing_edit_addons',
  BillingChangePlan = 'billing_change_plan',
  BillingEndPlan = 'billing_end_plan',
  BillingInvoiceView = 'billing_invoice_view',
  BillingInvoiceExport = 'billing_invoice_export',
  BillingNotifications = 'billing_notifications',
  PaymentMethodView = 'payment_method_view',
  PaymentMethodCreate = 'payment_method_create',
  PaymentMethodEdit = 'payment_method_edit',
  PaymentMethodDelete = 'payment_method_delete',
  AddressView = 'address_view',
  AddressCreate = 'address_create',
  AddressEdit = 'address_edit',
  AddressDelete = 'address_delete',
  IntegrationView = 'integration_view',
  IntegrationCreate = 'integration_create',
  IntegrationEdit = 'integration_edit',
  IntegrationDelete = 'integration_delete',
  ActivityLogView = 'activity_log_view',
  ActivityLogExport = 'activity_log_export',
  FieldMappingView = 'field_mapping_view',
  FieldMappingCreate = 'field_mapping_create',
  FieldMappingEdit = 'field_mapping_edit',
  FieldMappingDelete = 'field_mapping_delete',
  // Settings → Fields has two halves and they are two different capabilities.
  // Mapping needs a connected store and answers "which provider field fills this
  // Vera field"; Defaults needs no integration and answers "what does a product
  // inherit when it sets nothing". Added rather than renamed: `field_mapping_*`
  // keeps working untouched, so no grant is migrated and nobody is revoked.
  //
  // The edit half also repairs a regression. The scan defaults used to live on
  // Settings → Installation gated `manage_company`; moving them here left the save
  // on a permission only company_admin holds, so the nine other company roles that
  // can reach Fields could see the form and not save it.
  FieldDefaultView = 'field_default_view',
  FieldDefaultEdit = 'field_default_edit',
  // VERA-362 — add/verify/remove a custom email sending domain (SendGrid Domain
  // Authentication) and the per-company email link host in Settings → Features.
  // Sensitive: it changes how customer-facing mail is sent, so it is seeded
  // Admin-only (see 0088_settings_manage_brand_email.sql), unlike the
  // broadly-granted Settings permissions above it.
  SettingsManageBrandEmail = 'settings_manage_brand_email',
  // VRG-512 — seed, clear, or delete one record from the `intro` SAMPLE dataset: a
  // few obviously-labelled sample products, certificates and scans that let a
  // first-run console show something. Never billed (metering filters
  // `example_kind IS NULL`) and removable in one click.
  //
  // COMPANY ADMIN ONLY, FOR NOW, and the "for now" is the whole point. Admins are
  // currently the only role that receives the full Home intro tour, so they are the
  // only role for whom sample data means anything — every other role would be handed
  // records it was never walked through. When per-role tours are built, additional
  // roles are expected to get their own grant so they can clear their own samples.
  //
  // Deliberately an ACTION permission, not a `*_view`: reading whether samples exist
  // stays ungated on `GET /intro-data`, so a user without this still SEES the card and
  // a disabled button naming the permission (§27/§46) rather than an empty page.
  SampleDataManage = 'sample_data_manage',
  // VRG-654 — "Refer a brand": share the caller's referral link and send invite emails to
  // other brands. Granted to EVERY console company role by default (Spencer, 2026-10-03:
  // "every member, on by default") so a company admin can switch it off per role. An ACTION
  // permission: reading one's own referrals and the company totals stays on `view_company`,
  // and the full company list on `manage_company`, so §62's Ask decision does not apply.
  ReferralsSend = 'referrals_send',
  AiView = 'ai_view',
  AiUseCredits = 'ai_use_credits',
  AiPurchaseCredits = 'ai_purchase_credits',
  TagsInventoryView = 'tags_inventory_view',
  TagsInventoryEdit = 'tags_inventory_edit',
  // VRG-140 Slice 1 (VERA-891): receipt-scan a package to receive its tags
  // into on-hand inventory. Distinct from tags_inventory_edit (broader,
  // already granted to every company role) — receiving is the specific,
  // narrower default-role decision (company_admin/logistics/purchasing only).
  TagsInventoryReceive = 'tags_inventory_receive',
  TagsInventorySettings = 'tags_inventory_settings',
  TagsAutoOrder = 'tags_auto_order',
  TagsShopView = 'tags_shop_view',
  TagsShopPurchase = 'tags_shop_purchase',
  TagsOrderView = 'tags_order_view',
  TagsOrderExport = 'tags_order_export',
  ManageProduct = 'manage_product',
  ManageStore = 'manage_store',
  ProductView = 'product_view',
  ProductCreate = 'product_create',
  ProductEdit = 'product_edit',
  ProductDelete = 'product_delete',
  ProductExport = 'product_export',
  ProductImport = 'product_import',
  ViewCertificates = 'view_certificates',
  ViewProvenanceHistory = 'view_provenance_history',
  ManageCertificate = 'manage_certificate',
  // VRG-282 — bind a whole PACKAGE of tags to a product, which creates one inactive
  // certificate per tag in the box. Split out from `manage_certificate` rather than
  // reusing it because the act is bulk and physical: one call mints certificates for
  // every tag in a shipped box, so a brand may reasonably want staff who create
  // certificates one at a time but cannot commit a whole box. It is seeded ON
  // wherever `manage_certificate` is ON (see seed.ts) — splitting it out only makes it
  // possible to REVOKE independently later, never to withhold it by default.
  CertificateCreateFromPackage = 'certificate_create_from_package',
  // VRG-491 — mark a certificate's invalid scans as reviewed, which clears the
  // invalid-scan indicator on the certificates table and the certificate header until a
  // NEWER invalid scan arrives.
  //
  // Its own permission rather than `manage_certificate`, because suppressing a fraud
  // signal is a different capability from editing a certificate: a brand may reasonably
  // want staff who can create and edit certificates but cannot decide that suspected
  // counterfeit activity has been dealt with.
  //
  // Seeded to exactly the roles that hold `manage_certificate` — platform_admin,
  // platform_manager, company_admin, company_team_member — because creating certificates
  // is the capability this most closely tracks, and whoever mints a certificate is who
  // reviews the scans against it. Deliberately NOT mirrored from
  // `certificate_create_from_package`, which has drifted one role wider (company_purchasing
  // holds it without `manage_certificate`, VRG-400): purchasing binds boxes of tags to
  // products and has no reason to be able to dismiss a spoof signal.
  CertificateAcknowledgeInvalidScans = 'certificate_acknowledge_invalid_scans',
  // VRG-455 — record a sale to a named buyer WITHOUT naming a certificate. The
  // buyer's own authenticated scan resolves which physical item they hold and
  // completes the transfer.
  //
  // Its own permission, following the VRG-282 precedent above to the letter and
  // for the same reason: granted to EVERY role that already holds
  // `manage_certificate` and to no other, because recording a sale is an act of
  // committing certificates to a buyer, so anyone already trusted to create them
  // is trusted to do this. A default-off grant would ship the feature dead for
  // every existing role and turn "who can record a sale?" into a product question
  // nobody asked. The split exists for the FUTURE ability to revoke it
  // independently, not for a narrower default. Existing databases get the grant
  // via migration 0092 (the seed only runs on a destructive db:reset).
  RecordSale = 'record_sale',
  CertificateReplaceTag = 'certificate_replace_tag',
  CertificateActivate = 'certificate_activate',
  CertificateDelete = 'certificate_delete',
  TransferCertificate = 'transfer_certificate',
  // VERA-931 / VRG-528 — confirm that a dispatched certificate ARRIVED at one of the
  // company's own stores. Dispatch stays on `transfer_certificate`; only receipt splits out.
  //
  // WHY THE TWO HALVES OF ONE CUSTODY HOP GET DIFFERENT PERMISSIONS. Confirming arrival is
  // the billing event under the AT_LOCATION activation trigger, so the obvious move is to
  // gate it on `billing_activation_settings`. That is wrong, and expensively so: that
  // permission is seeded to company_admin and company_finance only, while the people who
  // physically confirm goods arrived are shop and logistics staff. Requiring Finance to
  // sign off on every delivery would leave goods unconfirmed — the failure mode is not "a
  // wrong charge", it is "nothing gets received".
  //
  // The financial decision was already made, ONCE, by whoever set the company's activation
  // trigger to AT_LOCATION — that choice is what `billing_activation_settings` correctly
  // gates. After it is made, "did the box turn up?" is an operational attestation, not a
  // financial authorisation, and it belongs to the people holding the box.
  //
  // Seeded to company_admin + company_logistics (product decision), plus platform_admin +
  // platform_manager so Vera staff operating inside a company workspace do not silently
  // lose a capability they hold today through `transfer_certificate`. Note company_logistics
  // does NOT hold `transfer_certificate`, so this permission is what finally lets the role
  // whose job this is actually do it.
  CustodyReceive = 'custody_receive',
  /**
   * VRG-591 — override the brand's "Certificate access" data-collection toggles
   * (require an email to view; ask for location) at CERTIFICATE scope.
   *
   * ## GATED BY LEVEL — each scope answers to the permission that already owns it
   *
   * | scope | gate |
   * |---|---|
   * | company (Settings -> Features) | whatever that page already carries — untouched |
   * | product | `manage_product` |
   * | certificate | THIS |
   *
   * So this governs the certificate level only; the product route carries
   * `manage_product`. Settled 2026-09-07 by the user, and it REPLACES an earlier
   * argument written here that rejected exactly this shape because every role able
   * to rename a record holds the manage permission. That argument is mechanically
   * true and was overruled: the override is treated as part of managing the record
   * it sits on.
   *
   * ## THE GRANT SET IS PARITY WITH `manage_certificate`, BY CONSTRUCTION
   *
   * platform_admin, platform_manager, company_admin, company_team_member — the
   * exact set holding `manage_certificate` when this was decided. Parity is the
   * rule and not the snapshot: a role that may edit a certificate may set that
   * certificate's access override, so this should move WITH `manage_certificate`
   * rather than be maintained as an independent list.
   *
   * The consequence, stated once and not re-argued: company_team_member can now
   * change the consent posture of an individual certificate. That is the accepted
   * cost of gating by level.
   *
   * ## WHY NOT `billing_activation_settings`, which gates the company toggles today
   *
   * Still no. The company route `PUT /certificate-trigger-settings/:id` writes the
   * activation trigger AND these two flags, so one BILLING permission ended up
   * gating a PRIVACY control. Activation decides WHEN A CUSTOMER IS CHARGED; these
   * decide WHETHER A VISITOR'S PERSONAL DATA IS COLLECTED, and Finance has no
   * standing on the second. Gating by level does not resurrect that conflation — it
   * points each scope at the permission that owns the RECORD, which for the company
   * scope is that page's own gate, not billing.
   */
  CertificateAccessSettings = 'certificate_access_settings',
  CertificateExport = 'certificate_export',
  // VRG-232: link/unlink certificates into a parent-child group (VRG-234's
  // Children section) and split a divisible group apart (VRG-235). Distinct
  // from ManageCertificate — grouping is a narrower, riskier operation (it can
  // change how a certificate's own QR/tag resolves, VRG-237) so it gets its
  // own gate rather than riding on the broad manage_certificate grant.
  ManageCertificateGroups = 'manage_certificate_groups',
  ViewCustomers = 'view_customers',
  InviteOwner = 'invite_owner',
  OwnerCreate = 'owner_create',
  OwnerEdit = 'owner_edit',
  DeleteCustomers = 'delete_customers',
  OwnerExport = 'owner_export',
  OwnerImport = 'owner_import',
  ViewAnalytics = 'view_analytics',
  AnalyticsExport = 'analytics_export',
  AnalyticsGeneral = 'analytics_general',
  AnalyticsRoleLogistics = 'analytics_role_logistics',
  AnalyticsRoleLegal = 'analytics_role_legal',
  AnalyticsRoleFinance = 'analytics_role_finance',
  AnalyticsRoleMarketing = 'analytics_role_marketing',
  AnalyticsRoleCompliance = 'analytics_role_compliance',
  /**
   * VRG-459 — write the brand-supplied figures (annual return-fraud loss, gross
   * margin, acquisition cost) that unblock `needs_input` metrics.
   *
   * There is no matching VIEW permission on purpose: anyone who can see analytics
   * sees the fields and their provenance, with the inputs disabled and a tooltip
   * naming this one (working-conventions §27). Hiding the figures a number rests on
   * from someone allowed to read the number would make the number unauditable.
   */
  AnalyticsInputEdit = 'analytics_input_edit',
  StudioView = 'studio_view',
  StudioCreate = 'studio_create',
  StudioEdit = 'studio_edit',
  StudioDelete = 'studio_delete',
  ViewPlatformSupervisors = 'view_platform_supervisors',
  InternalAnalyticsView = 'internal_analytics_view',
  // VRG-684 — every `/internal/gtm/*` page and the APIs behind them (Channels, SEO & AEO,
  // Articles, Sales + Outreach, Trade shows, Affiliates). GTM borrowed InternalAnalyticsView
  // as a placeholder from VRG-672 to VRG-679; Spencer gave it its own grant on 2026-10-08 so
  // a future sales hire can hold GTM without holding Analytics (and vice versa). Seeded to
  // platform_admin only, the same audience the placeholder had. Analytics keeps its own.
  InternalGtmView = 'internal_gtm_view',
  // VRG-685 — publish/unpublish vera.inc /insights articles and /learn pages from GTM → Articles.
  // Publishing goes live on the website within about a minute (the website reads the published
  // set from vera-2), so it is its own grant, apart from View GTM. Seeded to platform_admin only.
  InternalArticlesPublish = 'internal_articles_publish',
  // VRG-691 — add / edit sales leads, companies and deals on GTM → Sales.
  InternalSalesManage = 'internal_sales_manage',
  // VRG-740 — add / edit content ideas (and move them up to an approved draft) and VERAfied
  // guests on GTM → Articles. Seeded to platform_admin AND platform_manager (Spencer,
  // 2026-10-23). Writes also need InternalGtmView. Publishing stays InternalArticlesPublish.
  InternalContentIdeasManage = 'internal_content_ideas_manage',
  InternalPackageView = 'internal_package_view',
  InternalPackageUpload = 'internal_package_upload',
  // VRG-761 — hard-delete a package (box). The endpoint had NO permission at all, so
  // `RequireAdmin`'s implicit four-role list was its only gate; package.controller.ts has carried
  // a comment about exactly this gap since VRG-410, including why no existing package permission
  // may be borrowed for it (View is a read grant held by fulfilment, Upload is the create grant,
  // AssignDirectly decides ownership). Seeded to platform_admin ONLY — handling stock is not
  // destroying it, the same line VRG-253 drew for InternalAssignDirectly. Deleting a box now
  // DETACHES the chips inside it; it deletes only tags with no trace of use, and only when the
  // operator asks (see apps/api/src/database/tag-unused.ts).
  InternalPackageDelete = 'internal_package_delete',
  // VRG-253 — assign tags or a whole package DIRECTLY to a company, bypassing an order.
  // Separate from InternalPackageUpload because it is a different act: uploading stock is
  // inventory handling, whereas handing that stock to a named company is what decides who
  // may later scan it (the VRG-254 ownership guard reads exactly this). Internal admin only.
  InternalAssignDirectly = 'internal_assign_directly',
  InternalTagRetire = 'internal_tag_retire',
  InternalOrderView = 'internal_order_view',
  InternalOrderLink = 'internal_order_link',
  // VRG-426 — the cross-company invoice list at `/internal/invoices`. It had NO catalog
  // entry at all, so `invoices.internal.controller.ts` could not carry a `@Permissions`
  // decorator and its role list was the only gate — the one row in `internalAccess.ts`
  // with no permission behind it.
  //
  // Seeded to `platform_admin` + `platform_manager` (both reach the page today via that
  // role list and must not lose it) AND `vera_finance`: an invoice is the artifact of the
  // refunds and plan changes Finance already owns, so the role whose job this page is was
  // the one role locked out of it.
  InternalInvoiceView = 'internal_invoice_view',
  InternalSubscriptionView = 'internal_subscription_view',
  InternalCompanyCreate = 'internal_company_create',
  // VRG-306 (epic VRG-305) — provision an acquisition DEMO company from a
  // prospect's public website, for cold outbound.
  //
  // A dedicated permission rather than reusing `internal_company_create`, even
  // though both create a company row, because the two acts carry different risk:
  // creating a real company is routine, while provisioning a demo scrapes a third
  // party's imagery and hosts it on our infrastructure under their name. Keeping
  // it separate means it can be revoked on its own if the legal picture changes,
  // without taking normal company creation down with it.
  DemoProvision = 'demo_provision',
  InternalCompanyEnterSpace = 'internal_company_enter_space',
  InternalCompanyChangeSubscription = 'internal_company_change_subscription',
  InternalCompanySuspend = 'internal_company_suspend',
  InternalCompanyAddCertCredits = 'internal_company_add_cert_credits',
  InternalCompanyAddAiCredits = 'internal_company_add_ai_credits',
  InternalCompanyIssueRefund = 'internal_company_issue_refund',
  InternalIntegrationsView = 'internal_integrations_view',
  // VRG-226 — Support centre + boards.
  //
  // Opening a request and reading your OWN thread is deliberately NOT gated: a
  // permission that can stop someone asking for help is a support system that
  // fails exactly when it is needed. These two gate the wider surfaces instead —
  // seeing OTHER people's requests across the company, and casting a vote that
  // spends the company's share of influence on the roadmap.
  SupportRequestsViewCompany = 'support_requests_view_company',
  SupportRequestsVote = 'support_requests_vote',
  // Staff-side triage: the cross-company queue at /internal/support.
  InternalSupportManage = 'internal_support_manage',
  // VRG-221 — "What's new". Write, publish and edit announcements, and run the
  // "Notify requesters" fan-out that tells everyone who voted for or submitted a
  // shipped feature that it landed.
  //
  // There is deliberately NO company-facing counterpart. Gating the changelog would
  // mean a user who cannot see it also cannot learn that the thing they asked for
  // shipped, which defeats the feature. Per-announcement audience control is the
  // `announcements.required_permission` column instead, and it reuses the EXISTING
  // permissions above — an announcement about billing gates on `billing_view`.
  InternalAnnouncementsManage = 'internal_announcements_manage',
  // VRG-461 (epic VRG-445) — the internal market-data corpus at /internal/market.
  // Split view/manage, unlike InternalAnnouncementsManage's single gate, because this
  // is a research/GTM curation tool rather than a company-wide broadcast: reading the
  // corpus and adding/reviewing rows are different-risk acts (see
  // working-conventions §24 — the split mirrors InternalPackageView/Upload's shape,
  // not InternalAnnouncementsManage's). Granted to platform_admin + platform_manager;
  // see migration for the new-vs-preservation reasoning.
  InternalMarketIntelView = 'internal_market_intel_view',
  InternalMarketIntelManage = 'internal_market_intel_manage',
}
